import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PlusIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';
import { Trash2, Edit2 } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmDialog from '../components/ConfirmDialog';

const AdminScorecardMaxLoanPage = () => {
    const [rows, setRows] = useState([]);
    const [settings, setSettings] = useState({
        section_title: 'Calculate Your Maximum Home Loan Eligibility',
        section_subtitle: 'Use this maximum home loan eligibility calculator range for each loan type:',
        footer_text: 'Total loans with Aham cannot cross ₹50 Lakhs. Your final amount depends on your full application.'
    });
    const [loading, setLoading] = useState(true);
    const [editingRow, setEditingRow] = useState(null);
    const [isAdding, setIsAdding] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, rowId: null });

    const [formData, setFormData] = useState({
        loan_type: '',
        amount_range: '',
        max_tenure: '',
        max_ltv: '',
        display_order: 0,
        is_active: true
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [rowsRes, settingsRes] = await Promise.all([
                axios.get('/api/admin/eligibility-scorecard/max-loan/rows'),
                axios.get('/api/admin/eligibility-scorecard/max-loan/settings')
            ]);
            
            if (rowsRes.data.success) {
                setRows(rowsRes.data.data);
            }
            if (settingsRes.data.success && settingsRes.data.data) {
                setSettings({
                    section_title: settingsRes.data.data.section_title || '',
                    section_subtitle: settingsRes.data.data.section_subtitle || '',
                    footer_text: settingsRes.data.data.footer_text || ''
                });
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load max loan data');
        } finally {
            setLoading(false);
        }
    };

    const handleSettingsSave = async () => {
        try {
            setSavingSettings(true);
            await axios.put('/api/admin/eligibility-scorecard/max-loan/settings', settings);
            toast.success('Settings updated successfully');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to update settings');
        } finally {
            setSavingSettings(false);
        }
    };

    const handleEdit = (row) => {
        setEditingRow(row);
        setIsAdding(false);
        setFormData({
            loan_type: row.loan_type,
            amount_range: row.amount_range,
            max_tenure: row.max_tenure,
            max_ltv: row.max_ltv,
            display_order: row.display_order,
            is_active: row.is_active === 1 || row.is_active === true
        });
    };

    const handleCancel = () => {
        setEditingRow(null);
        setIsAdding(false);
        setFormData({
            loan_type: '',
            amount_range: '',
            max_tenure: '',
            max_ltv: '',
            display_order: 0,
            is_active: true
        });
    };

    const handleSaveRow = async () => {
        try {
            if (!formData.loan_type || !formData.amount_range || !formData.max_tenure || !formData.max_ltv) {
                toast.error('Please fill in all required fields');
                return;
            }

            if (isAdding) {
                const response = await axios.post('/api/admin/eligibility-scorecard/max-loan/rows', formData);
                if (response.data.success) {
                    toast.success('Row added successfully');
                    fetchData();
                    handleCancel();
                }
            } else {
                const response = await axios.put(`/api/admin/eligibility-scorecard/max-loan/rows/${editingRow.id}`, formData);
                if (response.data.success) {
                    toast.success('Row updated successfully');
                    fetchData();
                    handleCancel();
                }
            }
        } catch (error) {
            console.error('Error saving row:', error);
            toast.error('Failed to save row');
        }
    };

    const confirmDelete = async () => {
        try {
            const response = await axios.delete(`/api/admin/eligibility-scorecard/max-loan/rows/${deleteConfirm.rowId}`);
            if (response.data.success) {
                toast.success('Row deleted successfully');
                fetchData();
                setDeleteConfirm({ isOpen: false, rowId: null });
            }
        } catch (error) {
            console.error('Error deleting row:', error);
            toast.error('Failed to delete row');
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">Max Loan Eligibility Table</h1>
                    <p className="text-slate-500 mt-1">Manage the table items for the Maximum Home Loan Eligibility Section.</p>
                </div>
                {!isAdding && !editingRow && (
                    <button
                        onClick={() => setIsAdding(true)}
                        className="px-4 py-2 bg-[#283079] text-white rounded-lg hover:bg-[#1f2559] flex items-center gap-2 font-semibold shadow-md"
                    >
                        <PlusIcon className="h-5 w-5" />
                        Add New Row
                    </button>
                )}
            </div>

            {/* Section Settings */}
            {!isAdding && !editingRow && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                    <h2 className="text-lg font-bold text-slate-800 mb-4 text-center">Section Settings</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Section Title
                            </label>
                            <input
                                type="text"
                                value={settings.section_title}
                                onChange={(e) => setSettings({ ...settings, section_title: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#283079] focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Section Subtitle
                            </label>
                            <textarea
                                value={settings.section_subtitle}
                                onChange={(e) => setSettings({ ...settings, section_subtitle: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#283079] focus:border-transparent"
                                rows={2}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Footer Text
                            </label>
                            <textarea
                                value={settings.footer_text}
                                onChange={(e) => setSettings({ ...settings, footer_text: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#283079] focus:border-transparent"
                                rows={2}
                            />
                        </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                        <button
                            onClick={handleSettingsSave}
                            disabled={savingSettings}
                            className="bg-[#283079] hover:bg-[#1f2559] text-white px-6 py-2 rounded-xl font-semibold transition-all disabled:opacity-50 shadow-md"
                        >
                            {savingSettings ? 'Saving...' : 'Save Settings'}
                        </button>
                    </div>
                </div>
            )}

            {/* Row Form */}
            {(isAdding || editingRow) && (
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                    <h3 className="text-xl font-bold mb-6 text-slate-800 border-b pb-4">{isAdding ? 'Add New Row' : 'Edit Row'}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Loan Type</label>
                            <input
                                type="text"
                                value={formData.loan_type}
                                onChange={(e) => setFormData({ ...formData, loan_type: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#283079]"
                                placeholder="e.g. Home Purchase (New & Existing)"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Loan Amount Range</label>
                            <input
                                type="text"
                                value={formData.amount_range}
                                onChange={(e) => setFormData({ ...formData, amount_range: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#283079]"
                                placeholder="e.g. ₹5 Lakhs to ₹50 Lakhs"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Max Tenure</label>
                            <input
                                type="text"
                                value={formData.max_tenure}
                                onChange={(e) => setFormData({ ...formData, max_tenure: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#283079]"
                                placeholder="e.g. 20 years"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Max Loan-to-Value (LTV)</label>
                            <input
                                type="text"
                                value={formData.max_ltv}
                                onChange={(e) => setFormData({ ...formData, max_ltv: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#283079]"
                                placeholder="e.g. 75% of property value"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Display Order</label>
                            <input
                                type="number"
                                value={formData.display_order}
                                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#283079]"
                            />
                        </div>
                    </div>
                    
                    <div className="mb-6 flex items-center gap-3 bg-slate-50 p-4 rounded-xl">
                        <div className="flex items-center h-5">
                            <input
                                id="is_active"
                                type="checkbox"
                                checked={formData.is_active}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                className="w-5 h-5 text-[#283079] border-slate-300 rounded focus:ring-[#283079]"
                            />
                        </div>
                        <div className="flex flex-col">
                            <label htmlFor="is_active" className="text-sm font-semibold text-slate-800">
                                Active Status
                            </label>
                            <p className="text-xs text-slate-500">
                                When active, this row will be visible on the public scorecard page.
                            </p>
                        </div>
                    </div>

                    <div className="flex gap-3 mt-8 justify-end">
                        <button
                            onClick={handleCancel}
                            className="px-6 py-2.5 bg-slate-100 text-slate-700 font-semibold rounded-xl hover:bg-slate-200 flex items-center gap-2"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSaveRow}
                            className="px-6 py-2.5 bg-[#283079] text-white font-semibold rounded-xl hover:bg-[#1f2559] flex items-center gap-2 shadow-md"
                        >
                            {isAdding ? 'Save Row' : 'Update Row'}
                        </button>
                    </div>
                </div>
            )}

            {/* Rows Table */}
            {!isAdding && !editingRow && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-200">
                                    <th className="p-4 font-semibold text-slate-700 text-sm">Order</th>
                                    <th className="p-4 font-semibold text-slate-700 text-sm">Status</th>
                                    <th className="p-4 font-semibold text-slate-700 text-sm">Loan Type</th>
                                    <th className="p-4 font-semibold text-slate-700 text-sm">Amount Range</th>
                                    <th className="p-4 font-semibold text-slate-700 text-sm">Max Tenure</th>
                                    <th className="p-4 font-semibold text-slate-700 text-sm">Max LTV</th>
                                    <th className="p-4 font-semibold text-slate-700 text-sm">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="7" className="p-8 text-center text-slate-500">Loading...</td>
                                    </tr>
                                ) : rows.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="p-8 text-center text-slate-500">No rows found. Add one above.</td>
                                    </tr>
                                ) : (
                                    rows.map((row, index) => (
                                        <tr key={row.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                                            <td className="p-4 text-slate-500 text-sm font-medium">#{row.display_order}</td>
                                            <td className="p-4">
                                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${row.is_active ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-500'}`}>
                                                    {row.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="p-4 text-slate-800 text-sm font-medium">{row.loan_type}</td>
                                            <td className="p-4 text-slate-600 text-sm">{row.amount_range}</td>
                                            <td className="p-4 text-slate-600 text-sm">{row.max_tenure}</td>
                                            <td className="p-4 text-slate-600 text-sm">{row.max_ltv}</td>
                                            <td className="p-4">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => handleEdit(row)}
                                                        className="p-1.5 hover:bg-slate-200 rounded text-slate-500 hover:text-[#283079] transition-colors"
                                                        title="Edit"
                                                    >
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => setDeleteConfirm({ isOpen: true, rowId: row.id })}
                                                        className="p-1.5 hover:bg-red-100 rounded text-slate-500 hover:text-red-600 transition-colors"
                                                        title="Delete"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, rowId: null })}
                onConfirm={confirmDelete}
                title="Delete Row"
                message="Are you sure you want to delete this row? This action cannot be undone."
            />
        </div>
    );
};

export default AdminScorecardMaxLoanPage;
